import type { ReactNode } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import type {
  CreateModelProviderSecretRequest,
  UpdateModelProviderSecretRequest,
} from '@reflex/shared';
import { PageLayout, PageHeader } from '@/components/layout/PageLayout';
import { Button } from '@reflex/ui/components/ui/button';
import { Skeleton } from '@reflex/ui/components/ui/skeleton';
import { ModelProviderSecretsManager } from '@/components/secrets/ModelProviderSecretsManager';
import { NestedDetailHeader } from '@/components/shared/NestedDetailHeader';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import { usePermission } from '@reflex/ui/client/permissions';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useVisibleModelProviders } from '@/hooks/config/useVisibleModelProviders';

/**
 * Team-tier Model Providers page (`/{orgSlug}/org/teams/:teamId/model-providers`).
 */
export function TeamModelProvidersPage({ embedded = false }: { embedded?: boolean }) {
  const { teamId } = useParams<{ teamId: string }>();
  const id = teamId ?? '';
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id ?? '';
  const routes = useOrgRoutes();
  const orgKey = useOrgKey();
  // Checked within this team, matching the route gate: a member whose team
  // role carries `teams:manage` manages their own team's provider keys.
  const canManage = usePermission('teams:manage', { team: id });
  const queryClient = useQueryClient();
  const [addKeyOpen, setAddKeyOpen] = useState(false);

  const wrap = (content: ReactNode) =>
    embedded ? content : <PageLayout maxWidth="4xl">{content}</PageLayout>;

  const teamQuery = useQuery({
    queryKey: ['teams', id],
    queryFn: () => api.getTeam(id),
    enabled: !!id,
  });

  const mpsKey = queryKeys.teamModelProviderSecrets(orgKey, id);
  const mpsQuery = useQuery({
    queryKey: mpsKey,
    queryFn: () => api.listTeamModelProviderSecrets(orgId, id),
    enabled: !!id && !!orgId && canManage,
  });
  const visibleProviders = useVisibleModelProviders(mpsQuery.data ?? []);

  const createMutation = useMutation({
    mutationFn: (data: CreateModelProviderSecretRequest) =>
      api.createTeamModelProviderSecret(orgId, id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Team model provider key saved');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      secretId,
      data,
    }: {
      secretId: string;
      data: UpdateModelProviderSecretRequest;
    }) => api.updateTeamModelProviderSecret(orgId, id, secretId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Team model provider key updated');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (secretId: string) => api.deleteTeamModelProviderSecret(orgId, id, secretId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mpsKey });
      toast.success('Team model provider key deleted');
    },
    onError: (err: Error) =>
      toast.error('Failed to delete team model provider key', { description: err.message }),
  });

  const teamProvidersHeader = {
    iconVariant: 'box' as const,
    icon: <Sparkles className="h-5 w-5 text-muted-foreground" />,
    title: 'Team Model Providers',
    docs: 'administration/security#secrets-tiers',
  };

  if (!id) {
    return wrap(
      !embedded ? <PageHeader {...teamProvidersHeader} description="Missing team id." /> : null,
    );
  }

  if (teamQuery.isLoading) {
    return wrap(
      <>
        {!embedded && <PageHeader {...teamProvidersHeader} description="Loading team…" />}
        <Skeleton className="h-32 w-full" />
      </>,
    );
  }

  const team = teamQuery.data;
  if (teamQuery.isError || !team) {
    return wrap(
      <>
        {!embedded && (
          <NestedDetailHeader
            back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
            {...teamProvidersHeader}
            description="This team could not be loaded."
          />
        )}
      </>,
    );
  }

  const isPending =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return wrap(
    <>
      {!embedded && (
        <NestedDetailHeader
          back={{ to: routes.teams, label: 'Teams', ariaLabel: 'Back to teams' }}
          {...teamProvidersHeader}
          title={`${team.name} model providers`}
          description="Model-provider API keys owned by this team. Used for agents launched under this team's ownership when neither the launcher's personal keys nor a provider-specific override applies."
          actions={
            canManage ? (
              <Button aria-label="Add key" disabled={isPending} onClick={() => setAddKeyOpen(true)}>
                <Plus />
                <span className="sm:hidden">Add</span>
                <span className="hidden sm:inline">Add key</span>
              </Button>
            ) : undefined
          }
        />
      )}

      <ModelProviderSecretsManager
        title="Team API Keys"
        description={
          canManage
            ? 'Add team-wide model-provider keys here. Used by agents launched under this team when the launcher does not have a personal key for the provider.'
            : 'You do not have permission to manage team model providers.'
        }
        secrets={mpsQuery.data ?? []}
        providers={visibleProviders}
        isLoading={mpsQuery.isLoading}
        isPending={isPending}
        readOnly={!canManage}
        showSectionAction={embedded}
        addOpen={addKeyOpen}
        onAddOpenChange={setAddKeyOpen}
        emptyState={
          canManage
            ? 'No team model-provider keys yet. Add one to share credentials across this team.'
            : 'No team model-provider keys configured.'
        }
        onCreate={(provider, name, value, _scopeChoice, baseUrl) =>
          createMutation.mutateAsync({
            provider,
            type: 'apiKey',
            name,
            value,
            baseUrl,
          })
        }
        onUpdate={(secretId, patch) => updateMutation.mutateAsync({ secretId, data: patch })}
        onDelete={(secretId) => deleteMutation.mutate(secretId)}
      />
    </>,
  );
}
